import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class RuleEngineCoreService {
  private apiUrl = environment.ruleEngine.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Crea headers con tenant e customerId
   */
  private createHeaders(tenant: string, customerId: string): HttpHeaders {
    return new HttpHeaders({
      'x-tenant': tenant,
      'x-customer-id': customerId
    });
  }

  /**
   * GET /api/rules
   */
  getRules(tenant: string, customerId: string): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/rules`, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp || [])
    );
  }

  /**
   * GET /api/rules/:id
   */
  getRule(id: string, tenant: string, customerId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/rules/${id}`, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * POST /api/rules
   */
  createRule(payload: any, tenant: string, customerId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/rules`, payload, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * PATCH /api/rules/:id
   */
  updateRule(id: string, payload: any, tenant: string, customerId: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/rules/${id}`, payload, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * DELETE /api/rules/:id
   */
  deleteRule(id: string, tenant: string, customerId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/rules/${id}`, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * PATCH /api/rules/:id/enabled
   */
  setRuleEnabled(ruleId: string, enabled: boolean, tenant: string, customerId: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/rules/${ruleId}/enabled`, { enabled }, {
      headers: this.createHeaders(tenant, customerId)
    });
  }

  /**
   * GET /api/rule-types
   */
  getRuleTypes(): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/rule-types`).pipe(
      map(resp => resp?.data || resp || [])
    );
  }

  /**
   * GET /api/rule-types/:id
   */
  getRuleType(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/rule-types/${id}`).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * POST /api/testRuleEngine
   */
  testRuleEngine(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/testRuleEngine`, payload).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * POST /api/rules/:id/test
   * Test a specific existing rule with IoT message format
   */
  sendIoTMessage(message: any, tenant: string, customerId: string): Observable<any> {
    const ruleId = message.ruleId;
    if (!ruleId) {
      throw new Error('ruleId is required for testing a rule');
    }

    const payload = {
      deviceId: message.deviceId,
      machine: message.machine,       // NEW: Enriched machine object
      geofence: message.geofence,     // NEW: Enriched geofence object
      payload: message.payload,
      timestamp: message.timestamp || new Date().toISOString()
    };
    
    return this.http.post<any>(`${this.apiUrl}/rules/${ruleId}/test`, payload, {
      headers: this.createHeaders(tenant, customerId)
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * POST /api/utils/iotToFact
   */
  iotToFact(iotPayload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/utils/iotToFact`, iotPayload).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * GET /api/engine/stateCache
   */
  getCacheState(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/engine/stateCache`).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * GET /api/health
   */
  getHealth(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/health`);
  }

  /**
   * GET /api/notifications
   */
  getNotificationLogs(tenant: string, customerId: string, params?: any): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.apiUrl}/notifications?${queryString}` : `${this.apiUrl}/notifications`;
    return this.http.get<any>(url, {
      headers: this.createHeaders(tenant, customerId)
    });
  }

  /**
   * POST /api/notifications/:id/retry
   */
  retryNotification(notificationId: string, tenant: string, customerId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/notifications/${notificationId}/retry`, {}, {
      headers: this.createHeaders(tenant, customerId)
    });
  }

  /**
   * POST /api/rules/:id/testByRuleMessage
   * Test a specific rule with rule message format (modern endpoint)
   */
  testRuleWithRuleMessage(
    ruleId: string,
    ruleMessage: any,
    tenant: string,
    customerId: string
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/rules/${ruleId}/testByRuleMessage`,
      { fact: ruleMessage },  // Backend expects { fact: {...} }
      { headers: this.createHeaders(tenant, customerId) }
    ).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * GET /api/message-schemas/:id
   * Get a specific message schema by ID
   */
  getMessageSchema(id: string, tenant: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-tenant': tenant
    });
    return this.http.get<any>(`${this.apiUrl}/message-schemas/${id}`, {
      headers
    }).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * GET /api/engine/state/:subject/:ruleCode
   * Get the cached state for a subject+rule combination
   */
  getRuleState(subject: string, ruleCode: string): Observable<any> {
    console.log('[RuleEngineService] getRuleState called with subject:', subject, 'ruleCode:', ruleCode);
    console.log('[RuleEngineService] Subject charCodes:', Array.from(subject).map(c => c.charCodeAt(0)));
    
    const encodedSubject = encodeURIComponent(subject);
    const encodedRuleCode = encodeURIComponent(ruleCode);
    const url = `${this.apiUrl}/engine/state/${encodedSubject}/${encodedRuleCode}`;
    
    console.log('[RuleEngineService] Request URL:', url);
    
    return this.http.get<any>(url).pipe(
      map(resp => resp?.data || resp)
    );
  }

  /**
   * DELETE /api/engine/state/:subject
   * Delete all cached state for a subject (across all rules)
   */
  deleteSubjectState(subject: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/engine/state/${encodeURIComponent(subject)}`).pipe(
      map(resp => resp?.data || resp)
    );
  }
}
